/**
 * ACT2-1108: "Skip for now" and its Undo, for the setup steps an admin can skip (repos, context, invite).
 *
 * A skip is workspace-wide and stored on the server (`setup.skipStep`), so it hides the step for every
 * admin. Undo (`setup.undoSkip`) is offered until setup is complete; after that completion is final.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SkippableSetupStepId, WorkspaceSetupStatus } from '@/_shared/lib/setup-steps';

import { SETUP_PANEL_TOAST } from '../lib/panelToast';

interface SkipStepControlProps {
  step: SkippableSetupStepId;
  status: WorkspaceSetupStatus;
  /** The line before "Skip for now", e.g. "Don’t use repos for this?". */
  prompt: string;
}

const SKIPPED: Record<SkippableSetupStepId, keyof WorkspaceSetupStatus> = {
  repos: 'reposSkipped',
  context: 'contextSkipped',
  invite: 'inviteSkipped',
};

export function SkipStepControl({ step, status, prompt }: SkipStepControlProps): React.ReactElement | null {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const statusKey = orpc.setup.status.queryOptions().queryKey;
  const onSuccess = (result: { status: WorkspaceSetupStatus }): void => {
    queryClient.setQueryData(statusKey, result);
  };
  const onError = (err: Error): void => {
    toast.error(err.message || 'Failed to update the setup step', SETUP_PANEL_TOAST);
    void queryClient.invalidateQueries({ queryKey: statusKey });
  };
  const skip = useMutation(orpc.setup.skipStep.mutationOptions({ onSuccess, onError }));
  const undo = useMutation(orpc.setup.undoSkip.mutationOptions({ onSuccess, onError }));
  const pending = skip.isPending || undo.isPending;

  if (status[SKIPPED[step]] === true) {
    // Completion is final, so a skip cannot be undone after it.
    if (status.complete) return <p className="text-xs text-muted-foreground">Skipped for this workspace.</p>;
    return (
      <p className="text-xs text-muted-foreground">
        Skipped for this workspace.{' '}
        <Button
          type="button"
          variant="text"
          className="text-xs text-primary"
          label="Undo"
          aria-label={`Undo skipping this step`}
          disabled={pending}
          onClick={() => undo.mutate({ step })}
        />
      </p>
    );
  }
  if (status.complete) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {prompt}{' '}
      <Button
        type="button"
        variant="text"
        className="text-xs text-primary"
        label="Skip for now"
        disabled={pending}
        onClick={() => skip.mutate({ step })}
      />
    </p>
  );
}
